import dayjs from 'dayjs';
import { TAX_PAYMENT_PERIOD_TYPES_ENUM } from 'src/common/enums';
import { ExtendedPaymentPeriod, PeriodLicense } from 'src/types/tax';

export interface SingleLicenseTaxPeriodParams {
  address: string;
  applicationNumber: string;
  endDate: string;
  label: string;
  licenseNumber: string;
  startDate: string;
}

/**
 * Builds a PaymentPeriod with a single pre-selected license for the tax flow fast-path
 * (skip "select properties" step). Used when navigating to tax reporting from a specific
 * license context (dashboard action item, single-permit reporting history).
 */
export function buildSingleLicenseTaxPeriod(
  params: SingleLicenseTaxPeriodParams
): ExtendedPaymentPeriod {
  const {
    address,
    applicationNumber,
    endDate,
    label,
    licenseNumber,
    startDate,
  } = params;

  const license: PeriodLicense = {
    address,
    application_number: applicationNumber,
    applicationNumber,
    license: licenseNumber,
    ownerName: '',
    providers: [],
    selected: true,
    status: '',
    totPaidStatus: 'incomplete',
  };

  return {
    endDate,
    licenses: [license],
    name: label,
    startDate,
    year: dayjs(startDate).year(),
  };
}

/**
 * Builds a PaymentPeriod from a full ExtendedPaymentPeriod and a resolved license list.
 * Used in TaxSelectProperties where the full period and license data are available from Redux.
 */
export function buildMultiLicenseTaxPeriod(
  period: ExtendedPaymentPeriod,
  licenses: PeriodLicense[]
): ExtendedPaymentPeriod {
  return { ...period, licenses };
}

/**
 * Expands a period array for quarterly reporting when the config uses MONTHLY_QUARTERLY
 * and the user selected QUARTERLY as the period type.
 *
 * In that case each quarterly period is split into its three constituent months so the
 * downstream tax-report step receives one entry per month. For all other combinations
 * the input array is returned unchanged.
 */
export function expandQuarterlyPeriods(
  periods: ExtendedPaymentPeriod[],
  selectedPeriodType: TAX_PAYMENT_PERIOD_TYPES_ENUM | undefined,
  taxPaymentPeriod: TAX_PAYMENT_PERIOD_TYPES_ENUM
): ExtendedPaymentPeriod[] {
  if (
    taxPaymentPeriod !== TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY_QUARTERLY ||
    selectedPeriodType !== TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY
  ) {
    return periods;
  }

  return periods.flatMap((period) =>
    [0, 1, 2].map((offset) => {
      const date = dayjs(period.startDate).add(offset, 'month');
      return {
        ...period,
        endDate: date.endOf('month').format('YYYY-MM-DD'),
        licenses: period.licenses.filter((l) => l !== undefined),
        name: date.format('MMMM YYYY'),
        startDate: date.format('YYYY-MM-DD'),
      };
    })
  );
}
